export interface DecodedHash {
    hash: string;
    parameters: Record<string, string>;
}

export class URLParams {
    /**
     * Parse the fragment (aka hash) portion of a URI as an encoded combination
     * of a fragment followed by query parameters formatted as:
     *
     *    [ "#" fragment ] [ "?" query ]
     *
     * Example:
     *
     *    #exampleFragment?simpleKey=simpleValue&spacesInKey=value+with+spaces...
     *
     * Standard URI encoding is used for the query parameters portion of the fragment.
     *
     * Note that RFC 3986 defines URIs with this generic syntax:
     *
     *     URI         = scheme ":" hier-part [ "?" query ] [ "#" fragment ]
     *
     * Example:
     *
     *       foo://example.com:8042/over/there?name=ferret#nose
     *       \_/   \______________/\_________/ \_________/ \__/
     *        |           |            |            |        |
     *     scheme     authority       path        query   fragment
     *
     * RFC 3986 does not specifically preclude imbuing the fragment portion of a URI with
     * special encodings like query parameters, but this is by no means a common practice.
     *
     * Typically, query parameters are found in the query portion of a URI where they are
     * accessible to the server. In this case, we are using query parameters in the fragment
     * which is not transmitted to the server, but is accessible to client-side code.
     *
     * @param hash
     * @returns
     */
    static _parseHash(hash: string): DecodedHash {
        const encodedParametersHashFormat = /^(?<fragment>[^?]+)(?:\?(?<query>.+)?)?/;
        const encodedMatch = encodedParametersHashFormat.exec(hash);

        if (!encodedMatch || !encodedMatch.groups) {
            return {
                hash: "",
                parameters: {},
            };
        }

        const result = {
            hash: encodedMatch.groups.fragment || "",
            parameters: encodedMatch.groups.query
                ? Object.fromEntries(new URLSearchParams(encodedMatch.groups.query).entries())
                : {},
        };
        return result;
    }

    static generateHashString(decodedHash: DecodedHash): string {
        const queryParameters = new URLSearchParams(decodedHash.parameters).toString();
        const encodedHashString = `${decodedHash.hash}${queryParameters.length > 0 ? `?${queryParameters}` : ""}`;
        return encodedHashString;
    }

    static parseWindowLocationHash(): DecodedHash {
        const hashString = window.location.hash;
        return this._parseHash(hashString);
    }

    static unsetParam(key: string) {
        const decodedHash = this.parseWindowLocationHash();
        delete decodedHash.parameters[key];
        window.location.hash = this.generateHashString(decodedHash);
    }

    static setParam(key: string, value: string) {
        const decodedHash = this.parseWindowLocationHash();
        decodedHash.parameters[key] = value;
        window.location.hash = this.generateHashString(decodedHash);
    }

    static getParam(key: string): string | undefined {
        const decodedHash = this.parseWindowLocationHash();
        return decodedHash.parameters[key];
    }
}
